import type { BlueprintUnderlay } from '@shared/models/design/blueprint-underlay.model';

type BuildingScope = Pick<BlueprintUnderlay, 'buildingId' | 'site'>;

/**
 * True when {@link underlay} shows for {@link buildingId}: its own building, or
 * any building when the page has none. A site plan shows for none.
 */
export const isBlueprintUnderlayForBuilding = (
  underlay: BuildingScope,
  buildingId: string | undefined,
): boolean =>
  underlay.site !== true &&
  (underlay.buildingId === undefined || underlay.buildingId === buildingId);

/** The site plans in {@link underlays}, shown while no building is selected. */
export const getSiteBlueprintUnderlays = <T extends BuildingScope>(
  underlays: T[],
): T[] => underlays.filter((underlay) => underlay.site === true);

/**
 * The pages that show for {@link buildingId}. Same array when every page does.
 */
export const getBuildingBlueprintUnderlays = <T extends BuildingScope>(
  underlays: T[],
  buildingId: string | undefined,
): T[] =>
  underlays.every((underlay) =>
    isBlueprintUnderlayForBuilding(underlay, buildingId),
  )
    ? underlays
    : underlays.filter((underlay) =>
        isBlueprintUnderlayForBuilding(underlay, buildingId),
      );

/**
 * {@link underlays} without the pages that show for {@link buildingId}.
 */
export const removeBlueprintUnderlaysShownForBuilding = <
  T extends BuildingScope,
>(
  underlays: readonly T[],
  buildingId: string | undefined,
): T[] =>
  underlays.filter(
    (underlay) => !isBlueprintUnderlayForBuilding(underlay, buildingId),
  );

/**
 * True when {@link underlay} is among the pages listed next to {@link active}:
 * the site plans when {@link active} is one, else the pages of its building,
 * or of {@link buildingId} (the building in view) when {@link active} has
 * none. "All pages" scale changes stay within this set.
 */
export const isBlueprintUnderlaySiblingOf = (
  underlay: BuildingScope,
  active: BuildingScope,
  buildingId: string | undefined,
): boolean =>
  active.site === true
    ? underlay.site === true
    : isBlueprintUnderlayForBuilding(underlay, active.buildingId ?? buildingId);

/**
 * The pages in view: the site plans while no building is selected and the
 * project has one, else the pages that show for {@link buildingId}. A cleared
 * selection has no {@link buildingId}; in a project with one building, that
 * building's pages stand in for the missing site plan.
 */
export const getBlueprintUnderlaysInView = <T extends BuildingScope>(
  underlays: T[],
  view: {
    buildingId: string | undefined;
    buildingSelected: boolean;
    /** The project's building when it has exactly one. */
    soleBuildingId?: string;
  },
): T[] => {
  const site = view.buildingSelected
    ? []
    : getSiteBlueprintUnderlays(underlays);
  return site.length > 0
    ? site
    : getBuildingBlueprintUnderlays(
        underlays,
        view.buildingId ?? view.soleBuildingId,
      );
};

/** The id of the only building in {@link buildings}, else undefined. */
export const getSoleBuildingId = (
  buildings: readonly { id: string }[],
): string | undefined =>
  buildings.length === 1 ? buildings[0]?.id : undefined;
